/**
 * TroubleshootingTips - DIY tips before submitting maintenance request
 * Shows relevant troubleshooting steps based on issue description
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Lightbulb, 
  Clock, 
  Wrench, 
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import api from '../lib/api';

const TroubleshootingTips = ({ title, description, onProceed, onResolved }) => {
  const [tips, setTips] = useState([]);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [checkedTips, setCheckedTips] = useState({});

  useEffect(() => {
    if (title || description) {
      fetchTips();
    }
  }, [title, description]);

  const fetchTips = async () => {
    if (!title && !description) return;
    
    setLoading(true);
    try {
      const response = await api.post('/maintenance/tips/suggest', null, {
        params: { title, description }
      });
      
      if (response.data) {
        setTips(response.data.tips || []);
        setCategory(response.data.detected_category || 'general');
      }
    } catch (error) {
      console.error('Failed to fetch tips:', error);
      // Show generic tips on error
      setTips([
        {
          id: 'gen-1',
          title: 'Document the issue',
          description: 'Take photos and notes of the problem. This helps us diagnose and resolve it faster.',
          difficulty: 'easy',
          time_estimate: '5 minutes',
          diy_possible: true
        }
      ]);
      setCategory('general');
    } finally {
      setLoading(false);
    }
  };

  const toggleTip = (tipId) => {
    setCheckedTips(prev => ({
      ...prev,
      [tipId]: !prev[tipId]
    }));
  };

  const checkedCount = Object.values(checkedTips).filter(Boolean).length;
  const allChecked = checkedCount === tips.length && tips.length > 0;

  if (!title && !description) return null;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = () => {
    switch (category) {
      case 'plumbing': return '🔧';
      case 'electrical': return '⚡';
      case 'heating': return '🔥';
      case 'appliance': return '🏠';
      case 'locks_doors': return '🔐';
      case 'windows': return '🪟';
      case 'damp_mould': return '💧';
      case 'pest': return '🐛';
      default: return '🛠️';
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader 
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <Lightbulb className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-amber-800">
                Quick Troubleshooting Tips
              </CardTitle>
              <p className="text-sm text-amber-600 mt-1">
                {getCategoryIcon()} Detected: {category.replace(/_/g, ' ')} issue
              </p>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-amber-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-amber-600" />
          )}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" />
            </div>
          ) : (
            <>
              <p className="text-sm text-amber-700">
                Before submitting a request, try these quick fixes that might resolve your issue:
              </p>

              <div className="space-y-3">
                {tips.map((tip) => (
                  <div 
                    key={tip.id}
                    className={`p-3 rounded-lg border transition-all ${
                      checkedTips[tip.id] 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggleTip(tip.id)}
                        className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                          checkedTips[tip.id]
                            ? 'bg-green-500 text-white'
                            : 'border-2 border-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {checkedTips[tip.id] && <CheckCircle2 className="w-3 h-3" />}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className={`font-medium ${checkedTips[tip.id] ? 'text-green-700 line-through' : 'text-gray-900'}`}>
                            {tip.title}
                          </h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getDifficultyColor(tip.difficulty)}`}>
                            {tip.difficulty}
                          </span>
                          {tip.time_estimate && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Clock className="w-3 h-3" />
                              {tip.time_estimate}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{tip.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress indicator */}
              {tips.length > 0 && (
                <div className="pt-2">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Tips checked: {checkedCount}/{tips.length}</span>
                    {allChecked && (
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        All tips tried!
                      </span>
                    )}
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 transition-all"
                      style={{ width: `${(checkedCount / tips.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-amber-200">
                {checkedCount > 0 && (
                  <Button 
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    onClick={onResolved}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Issue Resolved - Cancel Request
                  </Button>
                )}
                <Button 
                  onClick={onProceed}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  {checkedCount > 0 ? 'Still Need Help - Submit Request' : 'Skip Tips & Submit Request'}
                </Button>
              </div>

              {checkedCount === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Trying these tips first may resolve your issue without waiting for a contractor.
                </p>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default TroubleshootingTips;
